import { useState, useEffect, useCallback, useRef } from 'react';
import {
  TextField,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  CircularProgress,
  ClickAwayListener
} from '@mui/material';
import { Search, TrendingFlat } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { logger } from '../utils/logger';
import { en } from '../translations/en';
import { useSearch } from '../hooks/useSearch';
import { featureFlags } from '../utils/featureFlags';

export const SearchBar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { searchTerm, setSearchTerm, suggestCombined } = useSearch();

  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Route to /search when the redesign flag is on, otherwise the legacy
  // /home search results UI. Read once on mount — flipping the flag mid-
  // session would require a full reload to take effect anyway.
  const searchRedesignEnabled = featureFlags.isEnabled('enableSearchPageRedesign');
  const searchPath = searchRedesignEnabled ? '/search' : '/home';

  // Global ⌘K / Ctrl+K focuses the navbar search input. Skips when the
  // user is already typing in a different editable element so we don't
  // hijack focus mid-keystroke.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'k' && e.key !== 'K') return;
      if (!(e.metaKey || e.ctrlKey)) return;
      const active = document.activeElement as HTMLElement | null;
      if (active) {
        const tag = active.tagName;
        const isEditable =
          tag === 'INPUT' || tag === 'TEXTAREA' || active.isContentEditable;
        if (isEditable && active !== inputRef.current) return;
      }
      e.preventDefault();
      inputRef.current?.focus();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Clear search term when navigating away from the search results page.
  // Prevents stale text persisting in the navbar after the user leaves.
  // Fixes #1062.
  const prevPathnameRef = useRef(location.pathname);
  useEffect(() => {
    const prevPathname = prevPathnameRef.current;
    prevPathnameRef.current = location.pathname;

    const wasOnSearchPage = prevPathname === searchPath;
    const isNowOnSearchPage = location.pathname === searchPath;

    if (wasOnSearchPage && !isNowOnSearchPage) {
      setSearchTerm('');
    }
  }, [location.pathname, setSearchTerm, searchPath]);

  // Debounced suggestion fetching - uses single combined endpoint to reduce network calls
  const fetchSuggestions = useCallback(async (query: string) => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      // Single API call to get combined suggestions from creators + keywords
      const combinedSuggestions = await suggestCombined(query);
      setSuggestions(combinedSuggestions);
    } catch (error) {
      logger.error('Failed to fetch suggestions', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, [suggestCombined]);

  // Debounce the suggestion fetching
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (searchTerm.length >= 2) {
      debounceRef.current = setTimeout(() => {
        fetchSuggestions(searchTerm);
      }, 300);
    } else {
      setSuggestions([]);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchTerm, fetchSuggestions]);

  // Navigate to home with search query
  const handleSearch = useCallback((query: string) => {
    if (!query.trim()) return;

    setShowDropdown(false);
    setSuggestions([]);
    navigate(`${searchPath}?q=${encodeURIComponent(query.trim())}`);
  }, [navigate, searchPath]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0 && suggestions[selectedIndex]) {
        handleSearch(suggestions[selectedIndex]);
      } else {
        handleSearch(searchTerm);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev =>
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
      setSuggestions([]);
      setSelectedIndex(-1);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setSelectedIndex(-1);
    setShowDropdown(true);
  };

  const handleSuggestionClick = (suggestion: string) => {
    setSearchTerm(suggestion);
    handleSearch(suggestion);
  };

  const handleClickAway = () => {
    setShowDropdown(false);
  };

  const shouldShowDropdown = showDropdown && (searchTerm.length >= 2 || isLoading);

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box
        data-testid="search-bar-wrapper"
        style={{ overflow: 'hidden', minWidth: 0 }}
        sx={{ position: 'relative' }}
      >
        <TextField
          inputRef={inputRef}
          placeholder={en.navigation.search}
          size="small"
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowDropdown(true)}
          sx={{
            width: '100%',
            maxWidth: isMobile ? '100%' : 450
          }}
          inputProps={{
            autoComplete: 'off',
            name: 'catalog-search',
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {isLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <Search
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleSearch(searchTerm)}
                  />
                )}
              </InputAdornment>
            ),
          }}
        />

        {shouldShowDropdown && (
          <Paper
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 1300,
              mt: 0.5,
              maxHeight: 400,
              overflow: 'auto',
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: theme.shadows[8]
            }}
          >
            <List sx={{ py: 0 }}>
              {/* Search for the typed term */}
              {searchTerm.trim() && (
                <ListItem
                  button
                  selected={selectedIndex === -1}
                  onClick={() => handleSearch(searchTerm)}
                  sx={{
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    '&:hover': {
                      bgcolor: theme.palette.action.hover
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Search color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography>
                        Search for &quot;<strong>{searchTerm}</strong>&quot;
                      </Typography>
                    }
                  />
                </ListItem>
              )}

              {/* Loading indicator */}
              {isLoading && (
                <ListItem>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        <Typography color="text.secondary">
                          Loading suggestions...
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              )}

              {/* Suggestions */}
              {!isLoading && suggestions.length > 0 && (
                <>
                  <Typography
                    variant="overline"
                    sx={{
                      px: 2,
                      py: 0.5,
                      display: 'block',
                      color: theme.palette.text.secondary,
                      bgcolor: theme.palette.grey[50]
                    }}
                  >
                    Suggestions
                  </Typography>
                  {suggestions.map((suggestion, index) => (
                    <ListItem
                      key={suggestion}
                      button
                      selected={selectedIndex === index}
                      onClick={() => handleSuggestionClick(suggestion)}
                      sx={{
                        '&:hover': {
                          bgcolor: theme.palette.action.hover
                        },
                        '&.Mui-selected': {
                          bgcolor: theme.palette.action.selected
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <TrendingFlat color="action" />
                      </ListItemIcon>
                      <ListItemText primary={suggestion} />
                    </ListItem>
                  ))}
                </>
              )}

              {/* No suggestions message */}
              {!isLoading && suggestions.length === 0 && searchTerm.length >= 2 && (
                <ListItem>
                  <ListItemText
                    primary={
                      <Typography color="text.secondary">
                        Press Enter to search
                      </Typography>
                    }
                  />
                </ListItem>
              )}
            </List>
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
};
